import {
    DatabaseOutlined,
    ExclamationCircleOutlined,
    SaveOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Divider,
    Form,
    Input,
    Modal,
    Space,
    Tabs,
    Typography
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
    clearDatabaseData,
    getActiveEnvironment,
    getAllEnvironments,
    saveEnvironment,
    validateEnvironment,
} from '../../services/environmentService';
import { syncAll } from '../../services/syncService';
import db from '../../db/database';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const EnvironmentConfigModal = ({ visible, onClose, onEnvironmentChanged }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [clearingDatabase, setClearingDatabase] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [currentEnvironment, setCurrentEnvironment] = useState(null);
    const [environments, setEnvironments] = useState([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);

    useEffect(() => {
        if (visible) {
            loadCurrentEnvironment();
            loadAllEnvironments();
        }
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCurrentEnvironment = async () => {
        try {
            const env = await getActiveEnvironment();
            setCurrentEnvironment(env);
            form.setFieldsValue({
                company: env.company,
                env: env.env,
                apiUrl: env.apiUrl,
                bcBaseUrl: env.bcBaseUrl,
                client_secret: env.client_secret,
                client_id: env.client_id,
                scope: env.scope,
                loginUrl: env.loginUrl,
            });
        } catch (error) {
            console.error('Error cargando entorno actual:', error);
        }
    };

    const loadAllEnvironments = async () => {
        try {
            const envList = await getAllEnvironments();
            setEnvironments(envList);
        } catch (error) {
            console.error('Error cargando entornos:', error);
        }
    };

    const handleSubmit = async (values) => {
        console.log('=== HANDLESUBMIT EJECUTÁNDOSE ===');
        console.log('Valores recibidos:', values);
        console.log('Estado actual - loading:', loading, 'clearingDatabase:', clearingDatabase, 'syncing:', syncing);

        try {
            console.log('Iniciando handleSubmit con valores:', values);
            setError('');
            setSuccess('');
            setLoading(true);

            // Validar configuración
            console.log('Validando configuración...');
            validateEnvironment(values);
            console.log('Configuración validada correctamente');

            // Verificar si hay cambios significativos
            const hasSignificantChanges = currentEnvironment && (
                currentEnvironment.company !== values.company ||
                currentEnvironment.env !== values.env ||
                currentEnvironment.bcBaseUrl !== values.bcBaseUrl ||
                currentEnvironment.client_id !== values.client_id ||
                currentEnvironment.client_secret !== values.client_secret
            );

            console.log('¿Hay cambios significativos?', hasSignificantChanges);

            if (hasSignificantChanges) {
                // Mostrar confirmación para cambios significativos
                console.log('Mostrando confirmación para cambios significativos...');
                setPendingValues(values);
                setShowConfirmation(true);
                setLoading(false); // Resetear loading mientras esperamos confirmación
            } else {
                // Guardar sin limpiar la base de datos
                console.log('Guardando configuración sin cambios significativos...');
                const result = await saveEnvironment(values);
                console.log('Configuración guardada:', result);
                setSuccess('Configuración guardada correctamente');
                if (onEnvironmentChanged) {
                    onEnvironmentChanged();
                }
            }
        } catch (error) {
            console.error('Error en handleSubmit:', error);
            setError(`Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const saveNewEnvironmentAndSync = async (values) => {
        try {
            setClearingDatabase(true);
            setSuccess('Limpiando base de datos...');

            // Limpiar base de datos
            await clearDatabaseData();

            // Guardar nueva configuración
            await saveEnvironment(values);

            setSuccess('Base de datos limpiada. Iniciando sincronización...');
            setSyncing(true);

            // Sincronizar con el nuevo entorno
            const syncResult = await syncAll();

            if (syncResult.success) {
                setSuccess('Configuración guardada y sincronización completada');
            } else {
                setError(`Configuración guardada pero error en sincronización: ${syncResult.message}`);
            }

            if (onEnvironmentChanged) {
                onEnvironmentChanged();
            }

            // Cerrar modal después de un momento
            setTimeout(() => {
                onClose();
            }, 2000);

        } catch (error) {
            setError(`Error durante el proceso: ${error.message}`);
        } finally {
            setClearingDatabase(false);
            setSyncing(false);
        }
    };

    const loadEnvironmentPreset = (env) => {
        form.setFieldsValue({
            company: env.company,
            env: env.env,
            apiUrl: env.apiUrl,
            bcBaseUrl: env.bcBaseUrl,
            client_secret: env.client_secret,
            client_id: env.client_id,
            scope: env.scope,
            loginUrl: env.loginUrl,
        });
    };

    const isProcessing = loading || clearingDatabase || syncing;

    // Función de prueba para diagnosticar problemas
    const testDatabaseConnection = async () => {
        try {
            console.log('Probando conexión a la base de datos...');

            // Probar acceso básico a la tabla
            const count = await db.environment.count();
            console.log('Número de entornos en la base de datos:', count);

            // Probar obtener todos los registros
            const allEnvs = await db.environment.toArray();
            console.log('Todos los entornos:', allEnvs);

            // Probar añadir un registro de prueba
            const testEnv = {
                company: 'Test Company',
                env: 'Test_Env',
                apiUrl: 'https://test.com',
                bcBaseUrl: 'https://test.com',
                grant_type: 'client_credentials',
                client_secret: 'test_secret',
                client_id: 'test_id',
                scope: 'test_scope',
                loginUrl: 'https://test.com/login',
                isActive: false,
                lastModified: new Date(),
            };

            const testId = await db.environment.add(testEnv);
            console.log('Registro de prueba añadido con ID:', testId);

            // Eliminar el registro de prueba
            await db.environment.delete(testId);
            console.log('Registro de prueba eliminado');

            setSuccess('Prueba de base de datos exitosa. Revisa la consola para más detalles.');
        } catch (error) {
            console.error('Error en prueba de base de datos:', error);
            setError(`Error en prueba de BD: ${error.message}`);
        }
    };

    // Función de debug para probar guardar directamente
    // const debugSaveEnvironment = async () => {
    //     try {
    //         console.log('=== DEBUG: Probando guardar entorno directamente ===');

    //         // Obtener valores actuales del formulario
    //         const formValues = form.getFieldsValue();
    //         console.log('Valores del formulario:', formValues);

    //         // Intentar guardar directamente
    //         setError('');
    //         setSuccess('');

    //         console.log('Llamando a saveEnvironment...');
    //         const result = await saveEnvironment(formValues);
    //         console.log('Resultado de saveEnvironment:', result);

    //         setSuccess('DEBUG: Configuración guardada exitosamente');

    //         // Recargar datos
    //         await loadCurrentEnvironment();
    //         await loadAllEnvironments();

    //     } catch (error) {
    //         console.error('DEBUG: Error al guardar:', error);
    //         setError(`DEBUG Error: ${error.message}`);
    //     }
    // };

    // Confirmar cambios significativos
    const handleConfirmChanges = async () => {
        try {
            console.log('Usuario confirmó cambios, ejecutando saveNewEnvironmentAndSync');
            setShowConfirmation(false);
            setLoading(true);
            await saveNewEnvironmentAndSync(pendingValues);
            setPendingValues(null);
        } catch (error) {
            console.error('Error al confirmar cambios:', error);
            setError(`Error: ${error.message}`);
            setLoading(false);
        }
    };

    // Cancelar cambios
    const handleCancelChanges = () => {
        console.log('Usuario canceló cambios');
        setShowConfirmation(false);
        setPendingValues(null);
        setLoading(false);
    };

    return (
        <Modal
            title={
                <Space>
                    <SettingOutlined />
                    Configuración del Entorno
                </Space>
            }
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            maskClosable={!isProcessing}
            closable={!isProcessing}
        >
            <Tabs defaultActiveKey="config">
                <TabPane tab="Configuración" key="config">
                    {error && (
                        <Alert
                            message={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {success && (
                        <Alert
                            message={success}
                            type="success"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {showConfirmation && (
                        <Alert
                            message="¿Confirmar cambio de entorno?"
                            description={
                                <div>
                                    <p>Al cambiar la configuración del entorno se borrará toda la base de datos local, incluyendo:</p>
                                    <ul>
                                        <li>Clientes</li>
                                        <li>Productos</li>
                                        <li>Facturas</li>
                                        <li>Usuarios</li>
                                        <li>Turnos, cajas y todos los demás datos</li>
                                    </ul>
                                    <p><strong>Esta acción no se puede deshacer.</strong></p>
                                    <div style={{ marginTop: 16 }}>
                                        <Space>
                                            <Button
                                                type="primary"
                                                danger
                                                onClick={handleConfirmChanges}
                                                loading={loading}
                                            >
                                                Sí, cambiar y sincronizar
                                            </Button>
                                            <Button onClick={handleCancelChanges}>
                                                Cancelar
                                            </Button>
                                        </Space>
                                    </div>
                                </div>
                            }
                            type="warning"
                            showIcon
                            icon={<ExclamationCircleOutlined />}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        disabled={isProcessing || showConfirmation}
                        onFinishFailed={(errorInfo) => {
                            console.error('Error en validación del formulario:', errorInfo);
                            setError('Error en la validación del formulario. Revisa los campos.');
                        }}
                    >
                        <Title level={5}>Business Central</Title>

                        <Form.Item
                            name="company"
                            label="Empresa"
                            rules={[{ required: true, message: 'La empresa es obligatoria' }]}
                        >
                            <Input placeholder="Nombre de la empresa en Business Central" />
                        </Form.Item>

                        <Form.Item
                            name="env"
                            label="Entorno"
                            rules={[{ required: true, message: 'El entorno es obligatorio' }]}
                        >
                            <Input placeholder="Nombre del entorno" />
                        </Form.Item>

                        <Form.Item
                            name="bcBaseUrl"
                            label="URL Base de Business Central"
                            rules={[
                                { required: true, message: 'La URL base es obligatoria' },
                                { type: 'url', message: 'Debe ser una URL válida' }
                            ]}
                        >
                            <Input placeholder="https://api.businesscentral.dynamics.com/v2.0/..." />
                        </Form.Item>

                        <Divider />

                        <Title level={5}>API de Autenticación</Title>

                        <Form.Item
                            name="apiUrl"
                            label="URL de la API"
                            rules={[
                                { required: true, message: 'La URL de la API es obligatoria' },
                                { type: 'url', message: 'Debe ser una URL válida' }
                            ]}
                        >
                            <Input placeholder="https://usuarios-api.app.elingenierojefe.es" />
                        </Form.Item>

                        <Form.Item
                            name="client_id"
                            label="Client ID"
                            rules={[{ required: true, message: 'El Client ID es obligatorio' }]}
                        >
                            <Input placeholder="Client ID de Azure AD" />
                        </Form.Item>

                        <Form.Item
                            name="client_secret"
                            label="Client Secret"
                            rules={[{ required: true, message: 'El Client Secret es obligatorio' }]}
                        >
                            <Input.Password placeholder="Client Secret de Azure AD" />
                        </Form.Item>

                        <Form.Item
                            name="scope"
                            label="Scope"
                            rules={[{ required: true, message: 'El scope es obligatorio' }]}
                        >
                            <Input placeholder="https://api.businesscentral.dynamics.com/.default" />
                        </Form.Item>

                        <Form.Item
                            name="loginUrl"
                            label="URL de Login"
                            rules={[
                                { required: true, message: 'La URL de login es obligatoria' },
                                { type: 'url', message: 'Debe ser una URL válida' }
                            ]}
                        >
                            <Input placeholder="https://login.microsoftonline.com/.../oauth2/v2.0/token" />
                        </Form.Item>

                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SaveOutlined />}
                                    loading={isProcessing}
                                    disabled={showConfirmation}
                                >
                                    {clearingDatabase ? 'Limpiando base de datos...' :
                                        syncing ? 'Sincronizando...' :
                                            loading ? 'Guardando...' : 'Guardar Configuración'}
                                </Button>
                                <Button
                                    type="default"
                                    onClick={testDatabaseConnection}
                                    disabled={isProcessing || showConfirmation}
                                >
                                    Probar BD
                                </Button>
                                {/* <Button
                                    type="dashed"
                                    onClick={debugSaveEnvironment}
                                    disabled={isProcessing || showConfirmation}
                                >
                                    Debug Guardar
                                </Button> */}
                                <Button onClick={onClose} disabled={isProcessing || showConfirmation}>
                                    Cancelar
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </TabPane>

                <TabPane tab="Entornos Guardados" key="saved">
                    <div>
                        <Title level={5}>Configuraciones Guardadas</Title>
                        <Text type="secondary">
                            Haga clic en una configuración para cargarla en el formulario
                        </Text>

                        {environments.length === 0 ? (
                            <Alert
                                message="No hay configuraciones guardadas"
                                type="info"
                                style={{ marginTop: 16 }}
                            />
                        ) : (
                            <div style={{ marginTop: 16 }}>
                                {environments.map((env) => (
                                    <div
                                        key={env.id}
                                        style={{
                                            border: '1px solid #d9d9d9',
                                            borderRadius: 6,
                                            padding: 12,
                                            marginBottom: 8,
                                            cursor: 'pointer',
                                            backgroundColor: env.isActive ? '#f6ffed' : '#fafafa',
                                            borderColor: env.isActive ? '#b7eb8f' : '#d9d9d9',
                                        }}
                                        onClick={() => loadEnvironmentPreset(env)}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <Text strong>{env.company}</Text>
                                                {env.isActive && <Text type="success"> (Activo)</Text>}
                                                <br />
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {env.env} - {new Date(env.lastModified).toLocaleString()}
                                                </Text>
                                            </div>
                                            <DatabaseOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabPane>
            </Tabs>
        </Modal>
    );
};

export default EnvironmentConfigModal; 